import {
  PersistedStoreState,
  StoreAction,
  StoreHydrationAction,
} from '@scoring-web/configs/store/store.config';
import clone from 'lodash/clone';
import omit from 'lodash/omit';
import pick from 'lodash/pick';

/**
 * Hydrate the store data from localStorage
 *
 * @param persistenceKey
 * @param state
 * @param targetKey
 * @returns
 */
export async function hydrateStore<StoreState extends PersistedStoreState>(
  persistenceKey: string,
  state: StoreState,
  targetKey?: keyof StoreState,
) {
  if (state.hydrated) return;

  Object.assign(state, { hydrated: true });

  const data = window?.localStorage?.getItem(persistenceKey);
  if (!data) return;

  const parsedData = JSON.parse(data);
  Object.assign(state, targetKey ? { [targetKey]: parsedData } : parsedData);
}

/**
 * Persist the store to localStorage
 *
 * @param persistenceKey
 * @param data
 */
export async function persistStore(persistenceKey: string, data: unknown) {
  const clonedData = clone(data);
  if (clonedData && typeof clonedData === 'object' && 'hydrated' in clonedData) {
    delete clonedData.hydrated;
  }

  window?.localStorage?.setItem(persistenceKey, JSON.stringify(clonedData));
}

/**
 * Generate hydration actions consisting of hydrate and persist action
 *
 * @param persistenceKey
 * @param state
 * @param targetKeys
 * @param included
 * @returns
 */
export function getStoreHydrationActions<StoreState extends PersistedStoreState>(
  persistenceKey: string,
  state: StoreState,
  targetKeys?: keyof StoreState | (keyof StoreState)[],
  included = true,
): StoreHydrationAction {
  const pickCallback = (...args: Parameters<typeof pick>) => {
    const result = pick(...args);
    const keys = args[1];

    return Array.isArray(keys) ? result : Object.values(result)?.[0];
  };
  const callback = included ? pickCallback : omit;

  return {
    hydrate: async () =>
      hydrateStore(persistenceKey, state, !Array.isArray(targetKeys) ? targetKeys : undefined),
    persist: async () =>
      persistStore(persistenceKey, targetKeys ? callback(state, targetKeys) : state),
  };
}

/**
 * Generate store base action
 *
 * @param state
 * @param initial
 * @returns
 */
export function getStoreBaseActions<State extends object>(
  state: State,
  initial: State,
): StoreAction<State> {
  return {
    reset: (passedInitial?: Partial<State>) => {
      Object.assign(state, passedInitial || initial);
    },
  };
}
